import { clearStorage as clearLivechatStorage } from '@inno-sdk/livechat-sdk'
import { createMiddlewareController } from '@inno-tools/api-middleware'
import { performCleanup__INTERNAL__ } from '@inno-tools/container-bridge'
import { CustomEvents } from 'shared/constants/customEvents'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { logger } from 'shared/services/logger'
import {
  verifyRefreshPersistence,
  withAuthStorageReason
} from 'shared/services/logger/utils/authDiagnostics'
import { dispatchCustomEvent } from 'shared/utils/customEvents'
import { decodeJWT, getTimeUntilExpiration } from 'shared/utils/jwt'
import { lsGetItem } from 'shared/utils/local-storage'

import { getHasOutdatedToken } from './auth/tokenState'

export const middlewareController = createMiddlewareController({
  authApiUrl: process.env.REACT_APP_AUTH_ENDPOINT!,

  // External-backend tokens (user.isExternalAuth) are opaque to the auth
  // service — never attach them to auth/container API calls and never try to
  // refresh them: a 401 with a matching token triggers a doomed refresh that
  // ends in a forced logout. Without a token the request simply goes out
  // unauthenticated, like any logged-out public visitor.
  getAccessToken: () => {
    const storedUser = lsGetItem(LocalStorageKey.USER)
    if (storedUser?.isExternalAuth) return undefined
    return (
      storedUser?.access_token ??
      lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)?.access_token ??
      undefined
    )
  },
  getRefreshToken: () => {
    const storedUser = lsGetItem(LocalStorageKey.USER)
    if (storedUser?.isExternalAuth) return undefined
    return (
      storedUser?.refresh_token ??
      lsGetItem(LocalStorageKey.BOOTSTRAP_TOKENS)?.refresh_token ??
      undefined
    )
  },

  onRefreshSuccess: ({ accessToken, refreshToken }) => {
    dispatchCustomEvent(CustomEvents.AUTH_TOKEN_UPDATED, {
      access_token: accessToken,
      refresh_token: refreshToken,
      redirectRoute: false
    })
    verifyRefreshPersistence(
      logger,
      accessToken,
      refreshToken,
      'api_middleware'
    )
  },

  onRefreshError: async () => {
    // Before logging out, check whether another tab already refreshed the
    // token. If localStorage holds a non-expired access token, adopt it.
    const currentStoredUser = lsGetItem(LocalStorageKey.USER)

    // A session backed by an external token has nothing to refresh against
    // the auth service — never force-logout it over a failed refresh.
    if (currentStoredUser?.isExternalAuth) return

    if (currentStoredUser?.access_token) {
      const decoded = decodeJWT(currentStoredUser.access_token)
      if (
        decoded?.exp &&
        getTimeUntilExpiration(decoded.exp) > 0 &&
        getHasOutdatedToken()
      ) {
        const { access_token, refresh_token } = currentStoredUser
        dispatchCustomEvent(CustomEvents.AUTH_TOKEN_UPDATED, {
          access_token,
          refresh_token,
          redirectRoute: false
        })
        return
      }
    }

    logger.error('auth.token_refresh_failed', {
      reason: 'refresh_request_failed'
    })
    logger.track('auth.logout', { reason: 'refresh_failed' })
    withAuthStorageReason('refresh_failed', () =>
      localStorage.removeItem(LocalStorageKey.USER)
    )
    dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
      user: null
    })
    await performCleanup__INTERNAL__()
    await clearLivechatStorage()
    window.location.href = '/'
  }
})
